import type { AoFormAccessor } from "$lib/yup"
import { ACCOUNT_TYPES } from "@tastyworks/shared-utils/misc/constants"
import {
  ACCOUNT_TYPE_CHOICES,
  AccountOpeningApplication,
  BUSINESS_ACCOUNT_SUBTYPES,
  ENTITY_ACCOUNT_TYPE_CHOICES,
  MARGIN_TYPES,
  choiceToAccountTypeMapping,
  type AccountTypeChoiceValue,
} from "@tastyworks/tastyworks-api"

export function isEntityAccount(
  accountType: ACCOUNT_TYPE_CHOICES | undefined
): boolean {
  return (
    accountType === ACCOUNT_TYPE_CHOICES.ENTITY ||
    accountType === ACCOUNT_TYPE_CHOICES.HEDGE_FUND
  )
}

export function isTrustAccount(
  entityAccountType: ENTITY_ACCOUNT_TYPE_CHOICES | undefined
): boolean {
  return entityAccountType === ENTITY_ACCOUNT_TYPE_CHOICES.TRUST
}

export function isMarginAccount(
  tradingMethod: MARGIN_TYPES | undefined
): boolean {
  return tradingMethod === MARGIN_TYPES.MARGIN
}

export function normalizeAccountTypeForApi(
  accountTypeName: ACCOUNT_TYPES
): ACCOUNT_TYPES {
  if (accountTypeName in BUSINESS_ACCOUNT_SUBTYPES) {
    return ACCOUNT_TYPES.BUSINESS
  }

  return accountTypeName
}

export function isProfessionalAccount(formAccessor: AoFormAccessor): boolean {
  const accountType = formAccessor.accountTypeName!
  if (accountType === ACCOUNT_TYPES.TRUST) {
    return !!formAccessor.trustAffiliations?.includes("isTrusteeCompensated")
  }
  // For business accounts we compare individual SSN to business SSN for LLC accounts.
  // This check on formData is temporary as this data doesn't persist through sessions, we'll
  // need backend to setup a service returning whether account is professional or not based on this logic.
  if (!(accountType in BUSINESS_ACCOUNT_SUBTYPES)) return false
  if (accountType !== ACCOUNT_TYPES.LLC) return true
  if (!formAccessor.taxNumber?.length) return false
  return formAccessor.businessTaxNumber !== formAccessor.taxNumber
}

export function syncAccountType(
  formAccessor: AoFormAccessor,
  accountType: AccountTypeChoiceValue | undefined
) {
  const derivedAccountType = deriveAccountType(accountType)
  if (!derivedAccountType) return

  const ACCOUNT_TYPE_NAME_KEY =
    "account.accountType.accountTypeSelect.accountTypeName"
  formAccessor.save(derivedAccountType, ACCOUNT_TYPE_NAME_KEY)
}

export function hasAtLeastOneNonEntityAccount(
  applications: AccountOpeningApplication[]
) {
  return applications.some(
    (application) =>
      !application.isEntity &&
      !application.isCanceled &&
      !application.isRejected
  )
}

export function deriveAccountType(
  accountType: AccountTypeChoiceValue | undefined
) {
  // Some account types, like "joint", need a more narrow subtype (e.g., "jointTenantsInCommon")
  // and would therefore not yet be mapped to the final account name expected
  // by the API (e.g., "Joint Tenants in Common")
  if (!accountType || !(accountType in choiceToAccountTypeMapping))
    return undefined

  return choiceToAccountTypeMapping[accountType]
}
